import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiPlus, FiDownload, FiAlertTriangle, FiCheckCircle, FiClock, FiFlag, FiMessageSquare, FiCheck,
} from "react-icons/fi";
import {
  getPips, getPip, createPip, activatePip, acknowledgePip, addPipCheckIn,
  updatePipMilestone, closePip, getPipCandidates, getPipReviewerOptions,
} from "../../services/pipApi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import useFormDraft from "../../hooks/useFormDraft";
import DraftNotice from "../../components/common/DraftNotice";
import {
  Card, PageHeader, Button, DataTable, Banner, Modal, FormField, Tabs, StatTile,
  Loader, ErrorState, EmptyState, ProgressBar, ConfirmDialog, EvidencePanel,
} from "../../components/common";
import { PersonCell } from "../../components/common/Avatar";
import ActivityTimeline from "../../components/goals/ActivityTimeline";
import {
  PIP_STATUS, PIP_TRIGGERS, PIP_DURATIONS, PIP_MILESTONE_STATUS, PIP_OUTCOMES,
} from "../../utils/constants";
import { downloadXlsx } from "../../utils/exportExcel";
import { formatDate, formatDateTime, todayISO } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";
import { VIEWS } from "../../services/navigation";
import { can } from "../../services/core";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { getPips, getPip, createPip, activatePip, acknowledgePip, addPipCheckIn, updatePipMilestone, closePip, getPipCandidates, getPipReviewerOptions };

const STATUS_TONE = {
  [PIP_STATUS.DRAFT]: "neutral",
  [PIP_STATUS.ACTIVE]: "info",
  [PIP_STATUS.EXTENDED]: "warning",
  [PIP_STATUS.SUCCESSFUL]: "success",
  [PIP_STATUS.UNSUCCESSFUL]: "danger",
  [PIP_STATUS.WITHDRAWN]: "neutral",
};

const MILESTONE_TONE = {
  [PIP_MILESTONE_STATUS.PENDING]: "neutral",
  [PIP_MILESTONE_STATUS.ON_TRACK]: "info",
  [PIP_MILESTONE_STATUS.AT_RISK]: "warning",
  [PIP_MILESTONE_STATUS.MET]: "success",
  [PIP_MILESTONE_STATUS.MISSED]: "danger",
};

const PipStatus = ({ status }) => (
  <span className={`badge badge-${STATUS_TONE[status] || "neutral"}`}><span className="badge-dot" />{status}</span>
);

const EMPTY_PIP = {
  employeeId: "", cycleId: "", trigger: PIP_TRIGGERS[0], reason: "", startDate: todayISO(),
  durationDays: 60, support: "", objectives: ["", "", ""], milestones: [], secondReviewerId: "",
};

// [NEW] Requirement 6: every plan has two reviewers.
// Reviewer 1 is always the employee's reporting manager (set by default, cannot be changed here).
// Reviewer 2 is picked from the list of managers and HR people (searchable dropdown).
function ReviewerFields({ employeeId, managerId, managerName, value, onChange }) {
  const options = useAsync(() => api.getPipReviewerOptions(employeeId), [employeeId], { skip: !employeeId });
  const [typing, setTyping] = useState(false);
  const all = options.data?.options || [];
  const list = all.filter((o) => o.id !== employeeId && o.id !== managerId);
  const managers = list.filter((o) => o.kind === "Manager");
  const hr = list.filter((o) => o.kind === "HR");
  const OTHER = "__other__";
  const known = list.some((o) => o.id === value);
  const hint = options.error
    ? `Could not load the list: ${options.error}`
    : options.data?.limited
      ? "Only your own manager and the cycle's HR owner can be listed for you. For someone else, choose Other and type their employee ID."
      : "Search and pick one manager or HR person.";
  return (
    <>
      <FormField label="Reviewer 1 (reporting manager)" hint="Set by default: the employee's reporting manager.">
        <input value={managerName || ""} disabled placeholder="Choose an employee first" />
      </FormField>
      <FormField label="Reviewer 2 (manager or HR)" required hint={hint}>
        <Select
          value={typing || (value && !known) ? OTHER : value}
          onChange={(e) => {
            if (e.target.value === OTHER) { setTyping(true); onChange(""); } else { setTyping(false); onChange(e.target.value); }
          }}
          disabled={!employeeId || options.loading}
          aria-label="Second reviewer"
        >
          <option value="">{!employeeId ? "Choose an employee first" : options.loading ? "Loading…" : "Choose the second reviewer"}</option>
          {managers.length > 0 && (
            <optgroup label="Managers">
              {managers.map((o) => <option key={o.id} value={o.id}>{o.name}{o.designation ? ` — ${o.designation}` : ""} ({o.id})</option>)}
            </optgroup>
          )}
          {hr.length > 0 && (
            <optgroup label="HR">
              {hr.map((o) => <option key={o.id} value={o.id}>{o.name}{o.designation ? ` — ${o.designation}` : ""} ({o.id})</option>)}
            </optgroup>
          )}
          <option value={OTHER} data-pin>Other: type an employee ID</option>
        </Select>
        {(typing || (value && !known)) && (
          <input
            style={{ marginTop: 8 }}
            value={value}
            onChange={(e) => onChange(e.target.value.trim())}
            placeholder="Employee ID of a manager or HR person, e.g. E1005"
            aria-label="Second reviewer employee ID"
          />
        )}
      </FormField>
    </>
  );
}

// [NEW] Requirement 6: "Start plan" pop-up for a draft plan (for example a draft created automatically from a low
// final rating). HR or the reporting manager starts it; the second reviewer is chosen here.
function StartPlanModal({ pip, open, onClose, onStarted }) {
  const [, setParams] = useSearchParams();
  const [second, setSecond] = useState("");
  const [busy, setBusy] = useState(false);
  if (!pip) return null;
  const start = async () => {
    if (!second) { toast.error("Choose the second reviewer (a manager or an HR person)."); return; }
    setBusy(true);
    try {
      const { data } = await api.activatePip(pip.id, second);
      toast.success("Plan started and shared with the employee and both reviewers.");
      setSecond("");
      onClose();
      // the draft may be replaced by a new plan carrying both reviewers: open that plan
      if (data?.pipId && String(data.pipId) !== String(pip.id)) setParams({ plan: String(data.pipId) });
      onStarted();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      size="md"
      title="Start plan"
      subtitle={`${pip.employeeName}: ${formatDate(pip.startDate)} to ${formatDate(pip.endDate)}`}
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={start} loading={busy} disabled={!second}>Start plan</Button>
        </>
      }
    >
      <div className="form-stack">
        <Banner tone="warning" title="The employee will see this plan">
          Starting the plan shares it with the employee, who must acknowledge it. Both reviewers can record check-ins.
        </Banner>
        <div className="form-grid">
          <ReviewerFields
            employeeId={pip.employeeId}
            managerId={pip.managerId}
            managerName={pip.managerName}
            value={second}
            onChange={setSecond}
          />
        </div>
      </div>
    </Modal>
  );
}

function CreatePipModal({ open, candidates, onClose, onCreated, cycleId }) {
  const [form, setForm] = useState(EMPTY_PIP);
  const [busy, setBusy] = useState(false);
  // [NEW] Keep an unfinished improvement plan (reason, objectives, support) in this browser.
  const draft = useFormDraft(`pip:new:${cycleId || ""}`, form, setForm, { active: open });

  const person = candidates.find((c) => c.employeeId === form.employeeId);

  const defaultMilestones = (days) => {
    const start = new Date(form.startDate);
    const points = days >= 90 ? [30, 60, 90] : days >= 60 ? [30, 60] : [30];
    return points.map((d) => ({
      title: `${d}-day review`,
      dueDate: new Date(start.getTime() + d * 86400000).toISOString().slice(0, 10),
    }));
  };

  const submit = async () => {
    if (!form.employeeId) { toast.error("Choose the employee this plan is for."); return; }
    if (form.reason.trim().length < 20) { toast.error("Write a clear reason, at least a sentence."); return; }
    const objectives = form.objectives.map((o) => o.trim()).filter(Boolean);
    if (objectives.length < 1) { toast.error("Add at least one measurable objective."); return; }
    if (!form.secondReviewerId) { toast.error("Choose the second reviewer (a manager or an HR person)."); return; }
    setBusy(true);
    try {
      await api.createPip({
        ...form,
        cycleId,
        objectives,
        milestones: form.milestones.length ? form.milestones : defaultMilestones(form.durationDays),
        employeeName: person?.employeeName,
        designationName: person?.designationName,
        departmentName: person?.departmentName,
        managerName: person?.managerName,
      });
      toast.success("Improvement plan created as a draft.");
      draft.clear();
      setForm(EMPTY_PIP);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setObjective = (index, value) =>
    setForm((f) => ({ ...f, objectives: f.objectives.map((o, i) => (i === index ? value : o)) }));

  return (
    <Modal
      open={open}
      size="lg"
      title="Start an improvement plan"
      subtitle="Set out what has to change, by when, and what support the person gets."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Create as draft</Button>
        </>
      }
    >
      <div className="form-stack">
        <DraftNotice draft={draft} />
        <Banner tone="warning" title="This is a formal record">
          The employee sees the plan once you activate it and must acknowledge it. Keep the wording factual and tied to
          measurable outcomes.
        </Banner>
        <div className="form-grid">
          <FormField label="Employee" required className="span-2">
            <Select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value, secondReviewerId: "" })}>
              <option value="">Choose an employee</option>
              {candidates.map((c) => (
                <option key={c.employeeId} value={c.employeeId} disabled={c.hasPip}>
                  {c.employeeName} — {c.designationName}{c.hasPip ? " (plan already exists)" : ""}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Trigger" required>
            <Select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}>
              {PIP_TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
          <FormField label="Duration" required>
            <Select value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value), milestones: [] })}>
              {PIP_DURATIONS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </Select>
          </FormField>
          <FormField label="Start date" required>
            <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value, milestones: [] })} />
          </FormField>
          <ReviewerFields
            employeeId={form.employeeId}
            managerId={person?.managerId}
            managerName={person?.managerName}
            value={form.secondReviewerId}
            onChange={(id) => setForm((f) => ({ ...f, secondReviewerId: id }))}
          />
          <FormField label="Why this plan is needed" required hint="Facts and numbers, not opinions." className="span-2">
            <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="For example: sprint attainment under 70% for two quarters, three defects traced to missing tests." />
          </FormField>
        </div>

        <div>
          <h4 className="section-title">Objectives the person must meet</h4>
          {form.objectives.map((objective, index) => (
            <FormField key={index} label={`Objective ${index + 1}`} className="span-2">
              <input value={objective} onChange={(e) => setObjective(index, e.target.value)} placeholder="Measurable, with a number and a date" />
            </FormField>
          ))}
          <Button variant="ghost" size="sm" icon={FiPlus} onClick={() => setForm((f) => ({ ...f, objectives: [...f.objectives, ""] }))}>
            Add another objective
          </Button>
        </div>

        <FormField label="Support the company provides" required hint="Training, coaching, workload changes.">
          <textarea rows={2} value={form.support} onChange={(e) => setForm({ ...form, support: e.target.value })} />
        </FormField>

        <p className="footnote">
          Review points are created automatically: {defaultMilestones(form.durationDays).map((m) => m.title).join(", ")}.
        </p>
      </div>
    </Modal>
  );
}

function PipDetail({ pip, canManage, canReview = canManage, canStart = canManage, canAcknowledge, onChanged }) {
  const [starting, setStarting] = useState(false); // [NEW] requirement 6: Start plan pop-up
  const [checkIn, setCheckIn] = useState(null);
  const [closing, setClosing] = useState(null);
  const [ack, setAck] = useState(false);
  const [ackComment, setAckComment] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      onChanged();
      setCheckIn(null);
      setClosing(null);
      setAck(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const met = pip.milestones.filter((m) => m.status === PIP_MILESTONE_STATUS.MET).length;
  const open = pip.status === PIP_STATUS.ACTIVE || pip.status === PIP_STATUS.EXTENDED;

  const exportPip = () =>
    downloadXlsx(`pip-${pip.employeeName.replace(/\s+/g, "-").toLowerCase()}`, [
      {
        name: "Plan",
        title: `Performance improvement plan — ${pip.employeeName}`,
        columns: [{ key: "field", label: "Field" }, { key: "value", label: "Value" }],
        rows: [
          { field: "Employee", value: pip.employeeName },
          { field: "Designation", value: pip.designationName },
          { field: "Manager", value: pip.managerName },
          { field: "HR owner", value: pip.hrOwner },
          { field: "Trigger", value: pip.trigger },
          { field: "Status", value: pip.status },
          { field: "Start date", value: formatDate(pip.startDate) },
          { field: "End date", value: formatDate(pip.endDate) },
          { field: "Duration (days)", value: pip.durationDays },
          { field: "Reason", value: pip.reason },
          { field: "Support provided", value: pip.support },
          { field: "Acknowledged", value: pip.acknowledgedAt ? formatDateTime(pip.acknowledgedAt) : "Not yet" },
          { field: "Outcome", value: pip.outcome || "Open" },
        ],
      },
      { name: "Objectives", columns: [{ key: "objective", label: "Objective" }], rows: pip.objectives.map((o) => ({ objective: o })) },
      {
        name: "Milestones",
        columns: [
          { key: "title", label: "Review point" }, { key: "dueDate", label: "Due", value: (r) => formatDate(r.dueDate) },
          { key: "status", label: "Status" }, { key: "progress", label: "Progress %" }, { key: "note", label: "Note" },
        ],
        rows: pip.milestones,
      },
      {
        name: "Check-ins",
        columns: [
          { key: "date", label: "Date", value: (r) => formatDate(r.date) }, { key: "by", label: "Held by" },
          { key: "ratingOfPeriod", label: "Period rating" }, { key: "managerNote", label: "Manager note" }, { key: "employeeNote", label: "Employee note" },
        ],
        rows: pip.checkIns,
      },
    ]);

  return (
    <>
      <div className="stat-grid">
        <StatTile label="Status" value={pip.status} tone={STATUS_TONE[pip.status]} icon={FiFlag} hint={pip.trigger} />
        <StatTile label="Progress" value={`${pip.progress}%`} icon={FiCheckCircle} tone={pip.progress >= 70 ? "success" : "warning"} hint={`${met}/${pip.milestones.length} review points met`} />
        <StatTile label="Days left" value={pip.daysLeft > 0 ? pip.daysLeft : 0} icon={FiClock} tone={pip.daysLeft < 15 ? "danger" : "info"} hint={`Ends ${formatDate(pip.endDate)}`} />
        <StatTile label="Check-ins" value={pip.checkIns.length} icon={FiMessageSquare} hint="Conversations recorded" />
      </div>

      {pip.status === PIP_STATUS.DRAFT && (
        <Banner
          tone="warning"
          title={pip.automatic ? "Draft created automatically from the final rating" : "Draft, not yet shared"}
          action={canStart && <Button size="sm" onClick={() => setStarting(true)} loading={busy}>Start plan</Button>}
        >
          The employee cannot see this plan until it is started. {canStart
            ? "Use Start plan to choose the second reviewer (a manager or HR); the reporting manager is reviewer 1."
            : "The reporting manager starts it and chooses the second reviewer."}
        </Banner>
      )}
      <StartPlanModal pip={pip} open={starting} onClose={() => setStarting(false)} onStarted={onChanged} />
      {open && !pip.acknowledgedAt && (
        <Banner
          tone="info"
          title="Waiting for the employee to acknowledge"
          action={canAcknowledge && <Button size="sm" onClick={() => setAck(true)}>Acknowledge</Button>}
        >
          Acknowledging records that the plan was received and discussed. It is not an admission of fault.
        </Banner>
      )}
      {pip.status === PIP_STATUS.SUCCESSFUL && <Banner tone="success" title="Closed successfully">{pip.outcomeSummary}</Banner>}
      {pip.status === PIP_STATUS.UNSUCCESSFUL && <Banner tone="danger" title="Closed as unsuccessful">{pip.outcomeSummary}</Banner>}
      {pip.status === PIP_STATUS.EXTENDED && <Banner tone="warning" title="Extended">{pip.outcomeSummary || `Extended to ${formatDate(pip.endDate)}.`}</Banner>}

      <div className="grid-2-1">
        <div className="stack">
          <Card title="Why this plan exists" actions={<Button variant="ghost" size="sm" icon={FiDownload} onClick={exportPip}>Excel</Button>}>
            <p>{pip.reason}</p>
            <h4 className="section-title">Objectives</h4>
            <ul className="icon-list">
              {pip.objectives.map((o) => <li key={o}>{o}</li>)}
            </ul>
            <h4 className="section-title">Support provided</h4>
            <p className="muted">{pip.support}</p>
            {pip.acknowledgedAt && (
              <p className="footnote">
                Acknowledged on {formatDateTime(pip.acknowledgedAt)}{pip.acknowledgeComment ? `: "${pip.acknowledgeComment}"` : "."}
              </p>
            )}
          </Card>

          <Card title="Review points" padded={false}>
            <ul className="milestone-list">
              {pip.milestones.map((m) => (
                <li key={m.id} className="milestone-row">
                  <div className="milestone-main">
                    <p className="milestone-title">{m.title}</p>
                    <p className="muted">Due {formatDate(m.dueDate)}{m.note ? ` · ${m.note}` : ""}</p>
                    <ProgressBar value={m.progress} size="sm" tone={m.status === PIP_MILESTONE_STATUS.MET ? "success" : m.status === PIP_MILESTONE_STATUS.AT_RISK ? "warning" : "primary"} showValue />
                  </div>
                  {(
                    <span className={`badge badge-${MILESTONE_TONE[m.status]}`}><span className="badge-dot" />{m.status}</span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="stack">
          <Card
            title="Check-ins"
            actions={canReview && open && <Button size="sm" icon={FiPlus} onClick={() => setCheckIn({ date: todayISO(), managerNote: "", employeeNote: "", ratingOfPeriod: "On Track", nextReviewDate: "", progress: Object.fromEntries(pip.milestones.map((m) => [m.id, m.progress])) })}>Add</Button>}
          >
            {pip.checkIns.length === 0 ? (
              <EmptyState title="No check-ins yet" text="Record each conversation so the plan has a paper trail." compact />
            ) : (
              <ActivityTimeline
                items={pip.checkIns.map((c) => ({
                  id: c.id,
                  action: `Check-in (${c.ratingOfPeriod})`,
                  actorName: c.by,
                  at: c.date,
                  comment: [c.managerNote, c.employeeNote && `Employee: ${c.employeeNote}`].filter(Boolean).join(" "),
                }))}
              />
            )}
          </Card>

          <EvidencePanel owner="pip" ownerId={pip.id} canUpload={open} title="Plan evidence" />

          <Card title="People on this plan">
            <ul className="kv-list">
              <li><span>Employee</span><strong>{pip.employeeName}</strong></li>
              <li><span>Designation</span><strong>{pip.designationName}</strong></li>
              <li><span>Reviewer 1 (reporting manager)</span><strong>{pip.managerName}</strong></li>
              {pip.reviewers.filter((r) => !r.primary).map((r, i) => (
                <li key={r.id}><span>Reviewer {i + 2}</span><strong>{r.name}</strong></li>
              ))}
              {pip.reviewers.filter((r) => !r.primary).length === 0 && (
                <li><span>Reviewer 2</span><strong>{pip.status === PIP_STATUS.DRAFT ? "Chosen at Start plan" : "—"}</strong></li>
              )}
              <li><span>HR owner</span><strong>{pip.hrOwner}</strong></li>
              <li><span>Period</span><strong>{formatDate(pip.startDate)} → {formatDate(pip.endDate)}</strong></li>
            </ul>
          </Card>

          {canManage && open && (
            <Card title="Close this plan">
              <p className="muted">Record the outcome once the final review point is done.</p>
              <div className="side-actions">
                {PIP_OUTCOMES.map((outcome) => (
                  <Button
                    key={outcome.value}
                    variant={outcome.tone === "danger" ? "danger" : outcome.tone === "success" ? "primary" : "secondary"}
                    block
                    onClick={() => setClosing({ outcome: outcome.value, summary: "", extendDays: 30 })}
                  >
                    {outcome.label}
                  </Button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal
        open={Boolean(checkIn)}
        size="md"
        title="Record a check-in"
        onClose={busy ? undefined : () => setCheckIn(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCheckIn(null)} disabled={busy}>Cancel</Button>
            <Button icon={FiCheck} loading={busy} onClick={() => act(() => api.addPipCheckIn(pip.id, checkIn), "Check-in recorded.")}>Save check-in</Button>
          </>
        }
      >
        {checkIn && (
          <div className="form-stack">
            <div className="form-grid">
              <FormField label="Date" required>
                <input type="date" value={checkIn.date} onChange={(e) => setCheckIn({ ...checkIn, date: e.target.value })} />
              </FormField>
              <FormField label="How is the period going?" required>
                <Select keepOrder value={checkIn.ratingOfPeriod} onChange={(e) => setCheckIn({ ...checkIn, ratingOfPeriod: e.target.value })}>
                  <option>On Track</option>
                  <option>At Risk</option>
                  <option>Off Track</option>
                </Select>
              </FormField>
            </div>
            <FormField label="Manager note" required>
              <textarea rows={3} value={checkIn.managerNote} onChange={(e) => setCheckIn({ ...checkIn, managerNote: e.target.value })} placeholder="What has changed, what still needs to change" />
            </FormField>
            <FormField label="Employee note" hint="What the employee said in the conversation">
              <textarea rows={2} value={checkIn.employeeNote} onChange={(e) => setCheckIn({ ...checkIn, employeeNote: e.target.value })} />
            </FormField>
            <h4 className="section-title">Progress on each objective (%)</h4>
            {pip.milestones.map((m) => (
              <FormField key={m.id} label={m.title}>
                <input
                  type="number" min="0" max="100"
                  value={checkIn.progress?.[m.id] ?? 0}
                  onChange={(e) => setCheckIn({ ...checkIn, progress: { ...checkIn.progress, [m.id]: e.target.value } })}
                />
              </FormField>
            ))}
            <FormField label="Next review on" hint={`Optional. Defaults to two weeks later, no later than ${formatDate(pip.endDate)}.`}>
              <input type="date" value={checkIn.nextReviewDate} max={pip.endDate} onChange={(e) => setCheckIn({ ...checkIn, nextReviewDate: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(closing)}
        size="md"
        title={closing?.outcome === "Extended" ? "Extend this plan" : `Close as ${closing?.outcome?.toLowerCase()}`}
        onClose={busy ? undefined : () => setClosing(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setClosing(null)} disabled={busy}>Cancel</Button>
            <Button
              variant={closing?.outcome === "Unsuccessful" ? "danger" : "primary"}
              loading={busy}
              onClick={() => act(() => api.closePip(pip.id, closing), closing.outcome === "Extended" ? "Plan extended." : "Plan closed.")}
            >
              {closing?.outcome === "Extended" ? "Extend" : "Close plan"}
            </Button>
          </>
        }
      >
        {closing && (
          <div className="form-stack">
            {closing.outcome === "Extended" && (
              <FormField label="Extend by" required>
                <Select value={closing.extendDays} onChange={(e) => setClosing({ ...closing, extendDays: Number(e.target.value) })}>
                  <option value={30}>30 days</option>
                  <option value={45}>45 days</option>
                  <option value={60}>60 days</option>
                </Select>
              </FormField>
            )}
            {closing.outcome === "Unsuccessful" && (
              <Banner tone="danger" title="Next step is with HR">
                Only the employee is notified automatically. Tell {pip.hrOwner} so they can take the next step. Make sure every check-in is recorded first.
              </Banner>
            )}
            <FormField label="Summary" required hint="What happened over the period and why you reached this outcome.">
              <textarea rows={4} value={closing.summary} onChange={(e) => setClosing({ ...closing, summary: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={ack}
        title="Acknowledge this plan?"
        confirmLabel="Acknowledge"
        loading={busy}
        onConfirm={() => act(() => api.acknowledgePip(pip.id, { comment: ackComment }), "Acknowledged.")}
        onClose={() => setAck(false)}
      >
        <p className="confirm-message">This records that you have read and discussed the plan with your manager.</p>
        <FormField label="Your comment" hint="Optional">
          <textarea rows={3} value={ackComment} onChange={(e) => setAckComment(e.target.value)} />
        </FormField>
      </ConfirmDialog>
    </>
  );
}

export default function PipPage() {
  const { cycleId, cycle } = useCycle();
  const { activeView, profile } = useRole();
  const [params, setParams] = useSearchParams();
  const pipId = params.get("plan");
  const [tab, setTab] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);

  const isEmployeeView = activeView === VIEWS.EMPLOYEE;
  const isManagerView = activeView === VIEWS.MANAGER;
  // [UPDATED] Improvement plans belong to the reporting manager: the manager creates, starts, extends and closes the plans
  // of their own team (backend app/api/pip.py, manager_only). HR only views every plan (and the automatic drafts are
  // created when HR finalises a rating in the PIP bands).
  const isReviewer = can(profile, "pip.review", ["TEAM", "SYSTEM_ALL"]);
  const isHrView = !isEmployeeView && !isManagerView;
  const canCreate = isManagerView && isReviewer; // "Start a plan" for a team member
  const ownsPlan = (plan) => isManagerView && isReviewer && plan?.managerId === profile?.id;
  const canReviewPlan = (plan) => ownsPlan(plan) || (!isEmployeeView && (plan?.reviewers || []).some((r) => r.id === profile?.id) && isReviewer);
  // [NEW] Requirement 6: HR starts a draft plan (the backend allows starting a plan with pip.manage only).
  const canStartPlan = (plan) => ownsPlan(plan);
  const scope = isEmployeeView ? "me" : isManagerView ? "team" : "org";

  const list = useAsync(() => api.getPips({ scope, cycleId }), [scope, cycleId]);
  const detail = useAsync(() => api.getPip(pipId), [pipId], { skip: !pipId });
  const candidates = useAsync(() => api.getPipCandidates({ cycleId }), [cycleId], { skip: isEmployeeView });

  // drafts replaced when they were started with a second reviewer are not shown
  const pips = (list.data || []).filter((p) => !p.replaced);
  const active = pips.filter((p) => p.status === PIP_STATUS.ACTIVE || p.status === PIP_STATUS.EXTENDED);
  const closed = pips.filter((p) => [PIP_STATUS.SUCCESSFUL, PIP_STATUS.UNSUCCESSFUL, PIP_STATUS.PARTIAL, PIP_STATUS.WITHDRAWN].includes(p.status));
  const drafts = pips.filter((p) => p.status === PIP_STATUS.DRAFT);
  const atRisk = active.filter((p) => p.milestones.some((m) => m.status === PIP_MILESTONE_STATUS.AT_RISK || m.status === PIP_MILESTONE_STATUS.MISSED));

  const shown = useMemo(
    () => ({ active, drafts, closed, candidates: candidates.data || [] }[tab] || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, pips, candidates.data]
  );

  const exportAll = () =>
    downloadXlsx(`improvement-plans-${cycle?.name || ""}`, [
      {
        name: "Plans",
        title: `Performance improvement plans — ${cycle?.name || ""}`,
        columns: [
          { key: "employeeName", label: "Employee" },
          { key: "designationName", label: "Designation" },
          { key: "departmentName", label: "Department" },
          { key: "managerName", label: "Manager" },
          { key: "trigger", label: "Trigger" },
          { key: "status", label: "Status" },
          { key: "startDate", label: "Start", value: (r) => formatDate(r.startDate) },
          { key: "endDate", label: "End", value: (r) => formatDate(r.endDate) },
          { key: "durationDays", label: "Days" },
          { key: "progress", label: "Progress %" },
          { key: "checkIns", label: "Check-ins", value: (r) => r.checkIns.length },
          { key: "outcome", label: "Outcome", value: (r) => r.outcome || "Open" },
        ],
        rows: pips,
      },
    ]);

  if (pipId) {
    return (
      <div className="page">
        <PageHeader
          title={detail.data ? `${detail.data.employeeName}: improvement plan` : "Improvement plan"}
          subtitle={detail.data ? `${detail.data.trigger}, ${formatDate(detail.data.startDate)} to ${formatDate(detail.data.endDate)}` : ""}
          backTo="/pip"
          backLabel="Back to plans"
          actions={detail.data && <PipStatus status={detail.data.status} />}
        />
        {detail.error && <ErrorState message={detail.error} onRetry={detail.reload} />}
        {!detail.data && !detail.error && <Loader />}
        {detail.data && (
          <PipDetail
            pip={detail.data}
            canManage={ownsPlan(detail.data)}
            canReview={canReviewPlan(detail.data)}
            canStart={canStartPlan(detail.data)}
            canAcknowledge={isEmployeeView}
            onChanged={() => { detail.reload(); list.reload(); }}
          />
        )}
      </div>
    );
  }

  if (isEmployeeView) {
    if (list.loading && !list.data) return <Loader />;
    const mine = active[0] || pips[0];
    return (
      <div className="page">
        <PageHeader title="My improvement plan" subtitle={cycle?.name} />
        {!mine ? (
          <Card>
            <EmptyState
              icon={FiCheckCircle}
              title="You are not on an improvement plan"
              text="If one is ever started for you, it will appear here with the objectives, the support you get and every check-in recorded."
            />
          </Card>
        ) : (
          <PipDetail pip={mine} canManage={false} canAcknowledge onChanged={list.reload} />
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Improvement plans"
        subtitle={`Formal PIPs for ${cycle?.name}, with objectives, review points and check-ins.`}
        actions={
          <>
            <Button variant="secondary" icon={FiDownload} onClick={exportAll} disabled={!pips.length}>Export Excel</Button>
            {canCreate && <Button icon={FiPlus} onClick={() => setCreateOpen(true)}>Start a plan</Button>}
          </>
        }
      />

      {isManagerView && (
        <Banner tone="info" title="Improvement plans of your team">
          A draft plan is created automatically when HR finalises a rating in the PIP bands (Scoring &amp; ratings), and you
          can also start a plan yourself. Open a draft and use <strong>Start plan</strong>: you are reviewer 1 and you choose
          reviewer 2 (a manager or HR person). You record check-ins, and extend or close the plan.
        </Banner>
      )}
      {isHrView && (
        <Banner tone="info" title="View only">
          Reporting managers create, start, extend and close improvement plans for their teams. HR sees every plan here.
        </Banner>
      )}

      <div className="stat-grid">
        <StatTile label="Active plans" value={active.length} icon={FiFlag} tone="info" hint="Running right now" />
        <StatTile label="At risk" value={atRisk.length} icon={FiAlertTriangle} tone="danger" hint="A review point missed or at risk" />
        <StatTile label="Drafts" value={drafts.length} icon={FiClock} tone="warning" hint="Not yet shared with the employee" />
        <StatTile label="Closed successfully" value={closed.filter((p) => p.status === PIP_STATUS.SUCCESSFUL).length} icon={FiCheckCircle} tone="success" hint="Improvement sustained" />
      </div>

      {atRisk.length > 0 && (
        <Banner tone="warning" title={`${atRisk.length} ${atRisk.length === 1 ? "plan needs" : "plans need"} attention`}>
          A review point has been missed or flagged at risk. Record a check-in before the next milestone date.
        </Banner>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "active", label: "Active", count: active.length },
          { key: "drafts", label: "Drafts", count: drafts.length },
          { key: "closed", label: "Closed", count: closed.length },
          { key: "candidates", label: "Suggested", count: (candidates.data || []).filter((c) => c.flagged && !c.hasPip).length },
        ]}
      />

      {tab === "candidates" ? (
        <Card padded={false} title="People the scores flag" subtitle="Low or falling performance in the last closed cycle. Starting a plan is still a judgement call.">
          <DataTable
            rows={(candidates.data || []).filter((c) => c.flagged)}
            rowKey="employeeId"
            emptyTitle="Nobody flagged"
            emptyText="Employees with a released final score below 60 appear here."
            columns={[
              { key: "employeeName", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={`${r.designationName}, ${r.departmentName}`} /> },
              { key: "managerName", label: "Manager" },
              { key: "finalScore", label: "Last score", align: "right" },
              { key: "rating", label: "Rating" },
              { key: "reason", label: "Why flagged", render: (r) => <span className="muted">{r.reason}</span> },
              {
                key: "act",
                label: "",
                align: "right",
                render: (r) =>
                  r.hasPip ? (
                    <span className="badge badge-info"><span className="badge-dot" />Plan exists</span>
                  ) : (
                    canCreate && <Button variant="secondary" size="sm" icon={FiPlus} onClick={() => setCreateOpen(true)}>Start plan</Button>
                  ),
              },
            ]}
          />
        </Card>
      ) : (
        <Card padded={false} title={{ active: "Active plans", drafts: "Draft plans", closed: "Closed plans" }[tab]}>
          {list.loading && !list.data ? <Loader /> : (
            <DataTable
              rows={shown}
              onRowClick={(row) => setParams({ plan: row.id })}
              emptyTitle="Nothing here"
              emptyText={tab === "active" ? "No plan is running at the moment." : tab === "drafts" ? "No drafts waiting to be activated." : "No plans have been closed yet."}
              columns={[
                { key: "employeeName", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={`${r.designationName}, ${r.departmentName}`} /> },
                { key: "trigger", label: "Trigger", render: (r) => <span className="cell-stack"><strong>{r.trigger}</strong><small>Manager: {r.managerName}</small></span> },
                { key: "period", label: "Period", render: (r) => <span className="cell-stack"><span>{formatDate(r.startDate)} → {formatDate(r.endDate)}</span><small>{r.durationDays} days</small></span> },
                { key: "progress", label: "Progress", width: "160px", render: (r) => <ProgressBar value={r.progress} size="sm" showValue tone={r.progress >= 70 ? "success" : "primary"} /> },
                { key: "status", label: "Status", render: (r) => <PipStatus status={r.status} /> },
                { key: "act", label: "", align: "right", render: () => <Button variant="secondary" size="sm">Open</Button> },
              ]}
            />
          )}
        </Card>
      )}

      <CreatePipModal
        open={createOpen}
        cycleId={cycleId}
        candidates={candidates.data || []}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { list.reload(); setTab("drafts"); }}
      />
    </div>
  );
}